import { Card } from "@marekl/adminer/components";
import { dayjs } from "@marekl/adminer/datetime";
import { formatCalendar, pluralizeCzech } from "@marekl/adminer/format";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import type { MembershipRole } from "../../../lib/queries/memberships";
import { FIELD_LABELS, FIELDS, displayValue, norm } from "./draftFields";
import { restoredRows, type DraftPayload } from "./draftPersist";
import type { DraftRow } from "./draftTypes";
import styles from "./SavedDraftPanel.module.css";

/**
 * The leader's rozpracovaná registrace, for somebody who may read it but not
 * write it: the membership office, mostly when the leader has asked for
 * help („jaké rodné číslo tam zadal?"). Read-only by decision; what the
 * office fixes, it fixes in the registry, and the leader's next open merges
 * it in (docs/rozpracovana-registrace.md §7).
 *
 * Shown as it was saved, not merged with the roster: the question is what
 * the leader MEANT.
 */
export function SavedDraftPanel({
  payload,
  updatedMs,
  updatedByName,
}: {
  payload: DraftPayload;
  updatedMs: number;
  updatedByName: string;
}) {
  const rows = restoredRows(payload);
  const n = rows.length;
  const saved = [formatCalendar(dayjs(updatedMs)), updatedByName].filter(Boolean).join(" · ");
  return (
    <Card>
      <Card.Header
        title="Rozpracovaná registrace vedoucího"
        subtitle={`Uloženo ${saved}. ${n} ${pluralizeCzech(n, ["osoba", "osoby", "osob"])}, jen ke čtení.`}
      />
      <Card.Content>
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.key} className={styles.item}>
              <span className={styles.name}>
                {`${r.person.last_name} ${r.person.first_name}`.trim() || "Nový člen"}
              </span>
              <span className={styles.kind}>{kindLabel(r)}</span>
              <ul className={styles.changes}>
                {changes(r).map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Card.Content>
    </Card>
  );
}

function kindLabel(r: DraftRow): string {
  switch (r.origin.kind) {
    case "persisted":
      return r.removed ? "odebrat ze soupisky" : "úprava";
    case "transfer":
      return r.origin.reregistered ? "znovu na soupisku" : "převod z minulé sezóny";
    default:
      return "nový člen";
  }
}

const roles = (rs: readonly MembershipRole[]) =>
  rs.length > 0 ? rs.map((x) => ROLE_LABEL[x]).join(", ") : "bez role";

function changes(r: DraftRow): string[] {
  if (r.removed) return [];
  const out: string[] = [];
  const base = r.baseline;
  for (const f of FIELDS) {
    const v = r.person[f];
    const conflict = r.conflicts[f];
    const open = conflict ? ` (nerozhodnuto, na soupisce „${displayValue(f, conflict.theirs)}“)` : "";
    if (!base) {
      if (norm(f, v) !== "" && f !== "country_code") out.push(`${FIELD_LABELS[f]}: ${displayValue(f, v)}`);
      continue;
    }
    const was = base[f] ?? "";
    const b = f === "birth_date" ? (was ? dayjs(was) : null) : was;
    if (norm(f, b) === norm(f, v)) continue;
    out.push(`${FIELD_LABELS[f]}: ${displayValue(f, b)} → ${displayValue(f, v)}${open}`);
  }
  const before = [...r.baselineRoles].sort().join();
  const after = [...r.roles].sort().join();
  if (r.origin.kind === "new" || r.origin.kind === "transfer") out.push(`Role: ${roles(r.roles)}`);
  else if (before !== after) out.push(`Role: ${roles(r.baselineRoles)} → ${roles(r.roles)}`);
  if (r.orphaned || r.duplicateOf) out.push("Čeká na rozhodnutí vedoucího: soupiska se mezitím změnila.");
  return out;
}
